"use client"

import { useEffect, useState, useRef } from "react"
import { Link } from "react-router-dom"
import { Calendar, Phone, Search, Filter, User, ChevronDown } from "lucide-react"
import SEO from "./SEO"

import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select"

const WP_API_BASE = "https://pranaam.pranaamhospitals.in/wp-json/wp/v2"
const INITIAL_LOAD = 100
const LOAD_MORE_COUNT = 12
const PRIORITY_IDS = [29, 31, 32, 33]
const PLACEHOLDER_IMAGE = "/placeholder.svg?height=300&width=300"

export default function DoctorsPage() {

  /* ================= FULL SEO ================= */

  const doctorsSchema = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalOrganization",
      "name": "Pranaam Hospitals",
      "url": "https://pranaam.pranaamhospitals.in/",
      "logo": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png",
      "description":
        "Pranaam Hospitals is a NABH-accredited multi-specialty hospital in Hyderabad with a team of highly experienced doctors and specialists.",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Hyderabad",
        "addressRegion": "Telangana",
        "addressCountry": "IN",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://pranaam.pranaamhospitals.in/doctors",
      },
      "headline": "Meet Our Doctors at Pranaam Hospitals",
      "description":
        "Explore the profiles of expert doctors and medical specialists at Pranaam Hospitals providing patient-centric healthcare in Hyderabad.",
      "author": {
        "@type": "Organization",
        "name": "Pranaam Hospitals",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Pranaam Hospitals",
        "logo": {
          "@type": "ImageObject",
          "url": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png",
        },
      },
      "datePublished": "2015-01-01",
      "dateModified": "2025-04-01",
    },
  ]

  /* ================= STATE ================= */

  const [doctors, setDoctors] = useState([])
  const [categories, setCategories] = useState([{ id: "all", name: "All Departments" }])
  const [locations] = useState([
    { id: "all", name: "All Locations" },
    { id: "Madinaguda", name: "Madinaguda" },
    { id: "Nallagandla", name: "Nallagandla" },
    { id: "Isnapur", name: "Isnapur" },
    { id: "Zaheerabad", name: "Zaheerabad" },
  ])

  const [searchQuery, setSearchQuery] = useState("")
  const [selectedDepartment, setSelectedDepartment] = useState("all")
  const [selectedLocation, setSelectedLocation] = useState("all")
  const [showExcellence, setShowExcellence] = useState(true)
  const [showLocation, setShowLocation] = useState(true)
  const [showDepartment, setShowDepartment] = useState(true)

  const [visibleCount, setVisibleCount] = useState(INITIAL_LOAD)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)

  const hasFetched = useRef(false)

  /* ================= FETCH CATEGORIES ================= */

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${WP_API_BASE}/categories?per_page=100`)
        const data = await res.json()

        const EXCLUDED_IDS = [44, 42, 311, 33, 36, 1]
        const filtered = data.filter((c) => !EXCLUDED_IDS.includes(c.id))

        setCategories([
          { id: "all", name: "All Departments" },
          ...filtered.map((c) => ({ id: c.id, name: c.name })),
        ])
      } catch (err) {
        console.error(err)
      }
    }

    fetchCategories()
  }, [])

  /* ================= FETCH DOCTORS ================= */

  useEffect(() => {
    if (hasFetched.current) return
    hasFetched.current = true

    const fetchDoctors = async () => {
      try {
        const res = await fetch(
          `${WP_API_BASE}/doctors?per_page=100&_embed=wp:featuredmedia`
        )
        const data = await res.json()

        const mapped = data.map((post) => {
          // Get the locations field from ACF - it could be a string or array
          let doctorLocations = post.acf?.locations || []
          // If it's a string, convert to array
          if (typeof doctorLocations === 'string') {
            doctorLocations = [doctorLocations]
          }
          
          return {
            id: post.id,
            name: post.title?.rendered || "",
            slug: post.slug,
            designation: post.acf?.designation || "",
            specialty: post.acf?.latest_achievement || "",
            categoryIds: post.categories || [],
            locations: doctorLocations,
            image:
              post._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
              PLACEHOLDER_IMAGE,
          }
        })

        const priorityMap = new Map()
        PRIORITY_IDS.forEach((id, index) => priorityMap.set(id, index))

        const priority = []
        const others = []

        mapped.forEach((doc) => {
          if (priorityMap.has(doc.id)) priority.push(doc)
          else others.push(doc)
        })

        priority.sort((a, b) => priorityMap.get(a.id) - priorityMap.get(b.id))
        others.sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        )

        setDoctors([...priority, ...others])
        setLoading(false)
      } catch (err) {
        console.error(err)
        setLoading(false)
      }
    }

    fetchDoctors()
  }, [])

  /* ================= FILTER ================= */

  const filteredDoctors = doctors.filter((doctor) => {
    const matchesSearch =
      doctor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doctor.specialty.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doctor.designation.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesDepartment =
      selectedDepartment === "all" ||
      doctor.categoryIds.includes(Number(selectedDepartment))

    const matchesLocation =
      selectedLocation === "all" ||
      (Array.isArray(doctor.locations) && doctor.locations.includes(selectedLocation))

    return matchesSearch && matchesDepartment && matchesLocation
  })

  const visibleDoctors = filteredDoctors.slice(0, visibleCount)

  /* ================= LOAD MORE ================= */

  const handleLoadMore = () => {
    setLoadingMore(true)
    setTimeout(() => {
      setVisibleCount((prev) => prev + LOAD_MORE_COUNT)
      setLoadingMore(false)
    }, 500)
  }

  /* ================= LOADER ================= */

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent"></div>
      </div>
    )
  }

  /* ================= RENDER ================= */

  return (
    <>
      <SEO
        title="Our Doctors | Expert Medical Specialists at Pranaam Hospitals Hyderabad"
        description="Meet the experienced doctors and medical specialists at Pranaam Hospitals."
        canonical="https://pranaam.pranaamhospitals.in/doctors"
        robots="index, follow"
        schema={doctorsSchema}
      />

      <div className="min-h-screen bg-white">
        <main>

          {/* Hero */}
          <section className="py-8 text-center">
            <h1 className="text-3xl font-bold text-primary">Doctors</h1>
          </section>

          {/* Search + Filters */}
          <section className="py-4">
            <div className="container mx-auto px-4">
              <div className="flex gap-8">
                {/* Sidebar */}
                <aside className="w-80 flex-shrink-0 hidden lg:block">
                  {/* Centre of Excellence */}
                  <div className="mb-6 border rounded-lg p-4">
                    <button
                      onClick={() => setShowExcellence(!showExcellence)}
                      className="flex items-center justify-between w-full mb-4"
                    >
                      <h3 className="text-lg font-bold">Centre of Excellence</h3>
                      <ChevronDown className={`h-5 w-5 transition-transform ${showExcellence ? '' : 'rotate-180'}`} />
                    </button>
                    {showExcellence && (
                      <>
                        <div className="relative mb-4">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                          <Input placeholder="Search" className="pl-10" />
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" className="w-4 h-4" />
                            <span className="text-sm">Child Care</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" className="w-4 h-4" />
                            <span className="text-sm">Women Care</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" className="w-4 h-4" />
                            <span className="text-sm">Fertility Care</span>
                          </label>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Location */}
                  <div className="mb-6 border rounded-lg p-4">
                    <button
                      onClick={() => setShowLocation(!showLocation)}
                      className="flex items-center justify-between w-full mb-4"
                    >
                      <h3 className="text-lg font-bold">Location</h3>
                      <ChevronDown className={`h-5 w-5 transition-transform ${showLocation ? '' : 'rotate-180'}`} />
                    </button>
                    {showLocation && (
                      <>
                        <div className="relative mb-4">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                          <Input placeholder="Search for Location" className="pl-10" />
                        </div>
                        <div className="space-y-2">
                          {locations.map((loc) => (
                            <label key={loc.id} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="location"
                                value={loc.id}
                                checked={selectedLocation === loc.id}
                                onChange={() => setSelectedLocation(loc.id)}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">{loc.name}</span>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Department */}
                  <div className="mb-6 border rounded-lg p-4">
                    <button
                      onClick={() => setShowDepartment(!showDepartment)}
                      className="flex items-center justify-between w-full mb-4"
                    >
                      <h3 className="text-lg font-bold">Department</h3>
                      <ChevronDown className={`h-5 w-5 transition-transform ${showDepartment ? '' : 'rotate-180'}`} />
                    </button>
                    {showDepartment && (
                      <>
                        <div className="relative mb-4">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                          <Input placeholder="Search" className="pl-10" />
                        </div>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {categories.map((dept) => (
                            <label key={dept.id} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="department"
                                value={String(dept.id)}
                                checked={selectedDepartment === String(dept.id)}
                                onChange={() => setSelectedDepartment(String(dept.id))}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">{dept.name}</span>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </aside>

                {/* Main Content */}
                <div className="flex-1">
                  <div className="relative mb-6">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Search doctors..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  {/* Mobile Filters */}
                  <div className="lg:hidden flex gap-2 mb-6 flex-wrap">
                    <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Department" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((dept) => (
                          <SelectItem key={dept.id} value={String(dept.id)}>
                            {dept.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Doctors Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {visibleDoctors.map((doctor) => (
                      <div key={doctor.id} className="p-4 border rounded-lg text-center">
                        <div className="w-full h-64 mb-4 overflow-hidden">
                          <img src={doctor.image} alt={doctor.name} className="w-full h-full object-cover" />
                        </div>
                        <h3 className="text-xl font-bold text-primary">{doctor.name}</h3>
                        <p className="text-sm text-gray-700">{doctor.designation}</p>
                        <Button asChild className="mt-3 bg-primary hover:bg-primary/90">
                          <Link to={`/doctors/${doctor.slug}`}>View Profile</Link>
                        </Button>
                      </div>
                    ))}
                    {loadingMore && [...Array(3)].map((_, i) => (
                      <div key={i} className="h-80 bg-gray-200 rounded-lg animate-pulse" />
                    ))}
                  </div>

                  {visibleCount < filteredDoctors.length && (
                    <div className="text-center mt-12">
                      <Button size="lg" variant="outline" disabled={loadingMore} onClick={handleLoadMore}>
                        {loadingMore ? "Loading..." : "Load More Doctors"}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

        </main>
      </div>
    </>
  )
}